import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, Flame, Zap, Dna, Target, Activity, Info, Sparkles, Eye, Droplet, Heart, Brain, Shield, Moon, Clock, Leaf, Battery, Dumbbell, Bone, Bandage, User, UserRound, Scissors, ZoomIn, X, Gem, Sun, FlaskConical, Cog, Scale, Utensils, TrendingUp } from 'lucide-react';
import { products, supplies, productSupplies } from '../mock';
import { referenceSections as refMap, globalNotice } from '../referenceData';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useInventory } from '../context/InventoryContext';
import ProductCard from '../components/ProductCard';
import Seo, { SITE_URL, SITE_NAME } from '../components/Seo';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);
  const { addItem } = useCart();
  const { has: wishHas, toggle: wishToggle } = useWishlist();
  const { getStock, getStatus } = useInventory();
  const [qty, setQty] = useState(1);
  const [variantIdx, setVariantIdx] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  React.useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setZoomOpen(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [zoomOpen]);

  if (!product) {
    return (
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
        <h1 className="font-serif-display text-4xl text-white mb-4">Specimen not found</h1>
        <Link to="/shop" className="font-mono-label text-[11px] text-[#ff2fa1]">← RESEARCH CATALOG</Link>
      </main>
    );
  }

  const related = products.filter(p => p.category === product.category && p.slug !== product.slug && !p.placeholder).slice(0, 4);
  const productExtra = productSupplies[product.slug] || [];
  const allSupplies = [...(supplies.STORAGE || []), ...productExtra];

  const activeVariant = product.variants ? product.variants[variantIdx] : null;
  const isPlaceholder = !!product.placeholder;
  const variantOOS = activeVariant && activeVariant.outOfStock;
  const variantPrice = activeVariant ? activeVariant.price : product.price;

  // Live inventory from backend (variant-aware).
  const liveStock = getStock(product.slug, activeVariant ? activeVariant.label : null);
  const liveStatus = getStatus(product.slug, activeVariant ? activeVariant.label : null);

  // Status precedence: explicit backend status is authoritative.
  // - OUT_OF_STOCK  → not purchasable, badge OUT OF STOCK
  // - PREORDER      → purchasable, badge COMING SOON · PRE-ORDER
  // - IN_STOCK      → purchasable if stock > 0
  const isOutOfStock = liveStatus === 'OUT_OF_STOCK' || variantOOS;
  const isPreorder = liveStatus === 'PREORDER';
  const isInStock = liveStatus === 'IN_STOCK' && liveStock > 0;

  // Can add to cart: not a placeholder, not out-of-stock/contact-for-pricing, and price known.
  const canPurchase = !isPlaceholder && !isOutOfStock && variantPrice !== null && (isPreorder || isInStock);

  const productRefs = refMap[product.slug] || [];

  const handleAdd = () => {
    if (isPlaceholder) {
      toast('This specimen is coming soon.');
      return;
    }
    if (isOutOfStock) {
      toast('This strength is currently out of stock.');
      return;
    }
    if (variantPrice === null) {
      toast('Contact us for pricing on this specimen.');
      return;
    }
    if (isInStock && qty > liveStock) {
      toast.error(`Only ${liveStock} left in stock.`);
      return;
    }
    addItem(product, qty, activeVariant, { preorder: isPreorder });
    toast.success(`${product.name} added to cart${isPreorder ? ' (Pre-order)' : ''}`);
  };

  // Build a concise, natural meta description from the product's own copy.
  const cleanDesc = (product.description || product.tagline || 'Research peptide from Vial Rat Research Co.')
    .replace(/\s+/g, ' ').trim();
  const metaDesc = (cleanDesc.length > 155 ? cleanDesc.slice(0, 152).trimEnd() + '…' : cleanDesc);

  // Product JSON-LD (factual only: name, description, image, brand, URL, offers).
  const offers = product.variants && product.variants.length
    ? product.variants.filter(v => typeof v.price === 'number').map(v => ({
        '@type': 'Offer',
        name: `${product.name} — ${v.label}`,
        price: String(v.price),
        priceCurrency: 'CAD',
        availability: (v.preorder || (typeof v.stock === 'number' && v.stock === 0)) ? 'https://schema.org/PreOrder' : 'https://schema.org/InStock',
        url: `${SITE_URL}/products/${product.slug}`
      }))
    : (typeof product.price === 'number'
        ? [{
            '@type': 'Offer',
            price: String(product.price),
            priceCurrency: 'CAD',
            availability: (product.preorder || product.stock === 0) ? 'https://schema.org/PreOrder' : 'https://schema.org/InStock',
            url: `${SITE_URL}/products/${product.slug}`
          }]
        : []);
  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: metaDesc,
    image: product.image,
    brand: { '@type': 'Brand', name: SITE_NAME },
    category: product.category,
    url: `${SITE_URL}/products/${product.slug}`,
    ...(offers.length ? { offers: offers.length === 1 ? offers[0] : offers } : {})
  };
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Research Catalogue', item: `${SITE_URL}/shop` },
      { '@type': 'ListItem', position: 3, name: product.name, item: `${SITE_URL}/products/${product.slug}` }
    ]
  };

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 pb-16">
      <Seo
        title={product.seoTitle || `${product.name} Research Peptide Canada | Vial Rat Research Co.`}
        description={metaDesc}
        canonicalPath={`/products/${product.slug}`}
        image={product.image}
        jsonLd={[productLd, breadcrumbLd]}
      />
      <Link to="/shop" className="inline-flex items-center gap-2 font-mono-label text-[11px] text-[#ff2fa1] hover:text-white transition-colors">
        <ArrowLeft size={14} /> RESEARCH CATALOG
      </Link>

      <div className="mt-8 grid md:grid-cols-2 gap-14">
        <div className="bg-black/40 rounded-xl border border-white/5 overflow-hidden group relative cursor-zoom-in" onClick={() => setZoomOpen(true)} role="button" aria-label="Zoom poster">
          <img src={product.image} alt={`${product.name} ${product.dose || ''} — ${product.category} — Vial Rat Research Co.`} className={`w-full h-auto ${isPlaceholder ? 'opacity-60' : ''}`} />
          <div className="absolute bottom-3 right-3 font-mono-label text-[10px] bg-black/70 backdrop-blur text-white px-3 py-1.5 rounded flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn size={12} /> CLICK TO ZOOM
          </div>
        </div>
        <div>
          <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">{product.category}</div>
          <h1 className="font-serif-display text-white text-6xl md:text-7xl">{product.name}</h1>
          {product.subtitle && (
            <div className="mt-2 font-mono-label text-[11px] text-[#ff2fa1] tracking-wider">{product.subtitle}</div>
          )}
          <div className="mt-3 font-mono-label text-[11px] text-neutral-400">{product.dose}</div>

          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <div className="font-serif-display text-white text-3xl">{activeVariant ? activeVariant.priceDisplay : product.priceDisplay}</div>
            {isPlaceholder ? (
              <span className="font-mono-label text-[10px] px-2 py-1 border border-[#ff2fa1]/40 text-[#ff2fa1] rounded">COMING SOON</span>
            ) : isOutOfStock ? (
              <span className="font-mono-label text-[10px] px-2 py-1 border border-white/30 text-neutral-400 rounded">OUT OF STOCK</span>
            ) : isPreorder ? (
              <span className="font-mono-label text-[10px] px-2 py-1 border border-[#ff2fa1]/40 text-[#ff2fa1] rounded">COMING SOON · PRE-ORDER</span>
            ) : (
              <span className="font-mono-label text-[10px] px-2 py-1 border border-emerald-500/30 text-emerald-400 rounded">IN STOCK · {liveStock} LEFT</span>
            )}
          </div>

          {product.variants && (
            <div className="mt-6 flex gap-3 flex-wrap">
              {product.variants.map((v, i) => {
                const vStatus = getStatus(product.slug, v.label);
                const vOOS = v.outOfStock || vStatus === 'OUT_OF_STOCK';
                const vPre = !vOOS && vStatus === 'PREORDER';
                return (
                  <button key={v.label} onClick={() => setVariantIdx(i)}
                    className={`font-mono-label text-[11px] px-4 py-2 rounded border transition-colors relative ${variantIdx === i ? 'bg-[#ff2fa1] border-[#ff2fa1] text-black' : vOOS ? 'border-white/10 text-neutral-500 hover:border-white/30' : 'border-white/15 text-neutral-300 hover:border-[#ff2fa1]'}`}>
                    {v.label}
                    {vOOS && <span className="block text-[9px] mt-0.5 opacity-80">OUT OF STOCK</span>}
                    {vPre && <span className="block text-[9px] mt-0.5 opacity-80">PRE-ORDER</span>}
                  </button>
                );
              })}
            </div>
          )}

          {!isPlaceholder && (
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <div className="flex items-center border border-white/15 rounded">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-neutral-400 hover:text-white"><Minus size={14} /></button>
                <span className="px-4 text-white">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-4 py-3 text-neutral-400 hover:text-white"><Plus size={14} /></button>
              </div>
              <button onClick={handleAdd} disabled={!canPurchase}
                data-testid="product-add-to-cart-btn"
                className={`px-8 py-3 rounded font-mono-label text-[11px] flex-1 transition-colors ${canPurchase ? 'btn-pink' : 'bg-white/10 text-neutral-500 cursor-not-allowed'}`}>
                {isOutOfStock ? 'OUT OF STOCK' : variantPrice === null ? 'CONTACT FOR PRICING' : isPreorder ? 'PRE-ORDER NOW' : 'ADD TO CART'}
              </button>
              <button onClick={() => { wishToggle(product.slug); toast.success(wishHas(product.slug) ? 'Removed from vault' : 'Saved to vault'); }}
                aria-label={wishHas(product.slug) ? 'Remove from vault' : 'Save to vault'}
                className={`w-12 h-12 rounded border flex items-center justify-center transition-colors ${wishHas(product.slug) ? 'border-[#ff2fa1] text-[#ff2fa1] bg-[#ff2fa1]/10' : 'border-white/15 text-neutral-300 hover:border-[#ff2fa1] hover:text-[#ff2fa1]'}`}>
                <Heart size={18} fill={wishHas(product.slug) ? '#ff2fa1' : 'transparent'} />
              </button>
            </div>
          )}

          {/* BUY 10 & SAVE 20% — eligible when variant is purchasable and priced */}
          {canPurchase && variantPrice !== null && !isPlaceholder && (
            <div className="mt-4 flex items-center gap-3 flex-wrap p-4 border border-[#ff2fa1]/25 bg-[#ff2fa1]/[0.04] rounded" data-testid="bulk10-block">
              <div className="flex-1 min-w-[200px]">
                <div className="font-mono-label text-[11px] text-[#ff2fa1]">BUY 10 &amp; SAVE 20% 🐀📦</div>
                <div className="text-[12px] text-neutral-300 mt-1 leading-snug">Stock up and save. Buy 10 of this exact strength for <span className="text-white font-semibold">${(variantPrice * 10 * 0.8).toFixed(2)}</span> <span className="text-neutral-500">(regular ${(variantPrice * 10).toFixed(2)})</span>. Bulk orders ship in ~10 days.</div>
              </div>
              <button
                data-testid="product-buy10-btn"
                onClick={() => { addItem(product, 10, activeVariant, { preorder: isPreorder }); toast.success(`Added 10 × ${product.name}${activeVariant ? ' ' + activeVariant.label : ''} — 20% off applied at cart`); }}
                className="btn-pink-outline px-5 py-2 rounded font-mono-label text-[11px] whitespace-nowrap">
                BUY 10 · SAVE 20%
              </button>
            </div>
          )}

          {/* Reta + GHK-Cu 50 mg — pair promo notice (Retatrutide product page) */}
          {product.slug === 'retatrutide' && (
            <div className="mt-4 p-4 border border-[#ff2fa1]/25 bg-[#ff2fa1]/[0.04] rounded" data-testid="reta-pair-notice">
              <div className="font-mono-label text-[11px] text-[#ff2fa1]">PAIR IT WITH GHK-Cu 🐀✨</div>
              <p className="text-[13px] text-neutral-300 mt-1 leading-snug">Add a <Link to="/products/ghk-cu" className="text-white underline underline-offset-2 hover:text-[#ff2fa1] transition-colors">50 mg GHK-Cu</Link> to your Reta order and save 25%.</p>
              <p className="text-[12px] text-neutral-400 mt-1">Only <span className="text-white font-semibold">$45</span> with Reta — regularly $60.</p>
            </div>
          )}

          {/* Reta + GHK-Cu — pair promo notice on GHK-Cu page (any variant selected) */}
          {product.slug === 'ghk-cu' && (
            <div className="mt-4 p-4 border border-[#ff2fa1]/25 bg-[#ff2fa1]/[0.04] rounded" data-testid="ghkcu-pair-notice">
              <div className="font-mono-label text-[11px] text-[#ff2fa1]">BUYING RETA? SAVE 25% 🐀✨</div>
              <p className="text-[13px] text-neutral-300 mt-1 leading-snug">Purchase any <Link to="/products/retatrutide" className="text-white underline underline-offset-2 hover:text-[#ff2fa1] transition-colors">Retatrutide</Link> and add one <span className="text-white">GHK-Cu 50 mg</span> for 25% off — only $45.</p>
            </div>
          )}

          {isPlaceholder && (
            <div className="mt-6">
              <button onClick={() => { wishToggle(product.slug); toast.success(wishHas(product.slug) ? 'Removed from vault' : 'Saved to vault'); }}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded font-mono-label text-[11px] border transition-colors ${wishHas(product.slug) ? 'border-[#ff2fa1] text-[#ff2fa1] bg-[#ff2fa1]/10' : 'border-white/15 text-neutral-300 hover:border-[#ff2fa1] hover:text-[#ff2fa1]'}`}>
                <Heart size={14} fill={wishHas(product.slug) ? '#ff2fa1' : 'transparent'} /> {wishHas(product.slug) ? 'SAVED TO VAULT' : 'SAVE TO VAULT'}
              </button>
            </div>
          )}

          {isPlaceholder && (
            <div className="mt-8 p-5 border border-[#ff2fa1]/30 bg-[#ff2fa1]/5 rounded">
              <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-2">COMING SOON</div>
              <p className="text-sm text-neutral-300">Full product details, pricing, imagery and research information will be added in a later update.</p>
            </div>
          )}

          {isPreorder && !isPlaceholder && (
            <div className="mt-8 p-5 border border-[#ff2fa1]/30 bg-[#ff2fa1]/5 rounded">
              <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-2">COMING SOON · PRE-ORDER AVAILABLE</div>
              <p className="text-sm text-neutral-300">This specimen is on the way. Reserve yours now and we'll ship it the moment inventory arrives. Interac e-Transfer only.</p>
              <p className="text-sm text-neutral-300 mt-3">
                Want to know when it's arriving?{' '}
                <a href="mailto:vialratpayments@gmail.com?subject=ETA%20Request%20-%20Pre-Order" className="text-[#ff2fa1] hover:text-white underline underline-offset-2 transition-colors">Contact us for the latest ETA</a>.
              </p>
            </div>
          )}

          <div className="mt-8 p-5 border border-[#ff2fa1]/20 bg-[#ff2fa1]/5 rounded">
            <div className="font-mono-label text-[11px] text-[#ff2fa1]">FOR RESEARCH USE ONLY</div>
            <p className="text-xs text-neutral-400 mt-2">Products are intended for laboratory and research purposes only. Not for human or veterinary use, food, or household application.</p>
          </div>

          <div className="mt-10 space-y-8">
            {product.storage && (
              <div>
                <h3 className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">STORAGE & HANDLING</h3>
                <p className="text-sm text-neutral-300 leading-relaxed">{product.storage}</p>
              </div>
            )}
            {product.description && (
              <div>
                <h3 className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">PRODUCT INFORMATION</h3>
                <p className="text-sm text-neutral-300 leading-relaxed whitespace-pre-line">{`${product.tagline || ''}${product.tagline ? '\n\n' : ''}${product.description}`}</p>
              </div>
            )}
            {product.research && product.research.length > 0 && (
              <div>
                <h3 className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">AREAS OF RESEARCH INTEREST</h3>
                <ul className="space-y-3">
                  {product.research.map((r, i) => (
                    typeof r === 'string'
                      ? <li key={i} className="text-sm text-neutral-300">— {r}</li>
                      : (
                        <li key={i} className="text-sm text-neutral-300 leading-relaxed">
                          <span className="text-white font-semibold">{r.title}</span> — {r.text}
                        </li>
                      )
                  ))}
                </ul>
                {product.researchStatus && (
                  <div className="mt-6 p-4 border border-white/10 bg-white/[0.03] rounded">
                    <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-2">RESEARCH STATUS</div>
                    <p className="text-sm text-neutral-300 leading-relaxed whitespace-pre-line">{product.researchStatus}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overview / What it is */}
      {product.overview && (() => {
        const iconMap = { Flame, Zap, Dna, Target, Activity, Sparkles, Eye, Droplet, Heart, Brain, Shield, Moon, Clock, Leaf, Battery, Dumbbell, Bone, Bandage, User, UserRound, Scissors, Gem, Sun, FlaskConical, Cog, Scale, Utensils, TrendingUp };
        const ov = product.overview;
        return (
          <section className="mt-20 max-w-4xl">
            <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">{ov.subtitle}</div>
            <h2 className="font-serif-display text-white text-4xl md:text-5xl">{ov.code}</h2>
            <p className="mt-6 text-neutral-300 leading-relaxed">{ov.intro}</p>
            <p className="mt-6 text-neutral-400 text-sm">{ov.leadIn}</p>
            <ul className="mt-6 space-y-4">
              {ov.bullets.map(b => {
                const Icon = iconMap[b.icon] || Activity;
                return (
                  <li key={b.title} className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-full bg-[#ff2fa1]/10 border border-[#ff2fa1]/30 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-[#ff2fa1]" />
                    </div>
                    <div>
                      <div className="text-white font-semibold text-base">{b.title}</div>
                      <div className="text-sm text-neutral-400 mt-0.5">{b.text}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
            {ov.highlight && (
              <div className="mt-8 p-5 border border-white/10 bg-white/[0.03] rounded">
                <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-2">{ov.highlight.label}</div>
                <p className="text-sm text-neutral-300 leading-relaxed">{ov.highlight.text}</p>
              </div>
            )}
            {ov.note && (
              <p className="mt-6 text-sm text-neutral-400 italic leading-relaxed">{ov.note}</p>
            )}
            {ov.disclaimer && (
              <p className="mt-6 font-mono-label text-[10px] text-[#ff2fa1]">{ov.disclaimer}</p>
            )}
          </section>
        );
      })()}

      {/* Related supplies */}
      <section className="mt-20">
        <h2 className="font-serif-display text-white text-3xl mb-3">You May Also Need</h2>
        <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-8">BENCH SUPPLIES</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {allSupplies.map(s => (
            <a key={s.title} href={s.url} target="_blank" rel="noreferrer" className="group product-card block bg-[#101013] border border-white/5 rounded-lg overflow-hidden p-4">
              <div className="aspect-square bg-white rounded overflow-hidden">
                <img src={s.img} alt={s.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
              </div>
              <div className="mt-4 text-white text-sm leading-snug">{s.title}</div>
              <div className="mt-3 font-mono-label text-[10px] text-[#ff2fa1]">VIEW ON AMAZON →</div>
            </a>
          ))}
        </div>
      </section>

      {/* Research-use notice + contact CTA */}
      <section className="mt-20">
        <div className="max-w-4xl p-6 md:p-8 border border-[#ff2fa1]/20 bg-[#ff2fa1]/5 rounded">
          <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">FOR LABORATORY RESEARCH USE ONLY</div>
          <p className="text-sm text-neutral-300 leading-relaxed">
            For laboratory research purposes only. Not for human consumption or therapeutic use.
          </p>
          <p className="mt-3 text-sm text-neutral-300 leading-relaxed">
            For product specifications, research documentation, testing information, or other product-related questions, please{' '}
            <a href="mailto:vialratpayments@gmail.com" className="text-[#ff2fa1] hover:text-white underline underline-offset-2 transition-colors">contact us</a>.
          </p>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-serif-display text-white text-3xl mb-8">Related Specimens</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {related.map(p => <ProductCard key={p.slug} product={p} />)}
          </div>
        </section>
      )}

      {/* Poster zoom lightbox */}
      {zoomOpen && (
        <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-in fade-in duration-200" onClick={() => setZoomOpen(false)} role="dialog" aria-modal="true">
          <button aria-label="Close" onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-[#ff2fa1] hover:text-black text-white flex items-center justify-center transition-colors">
            <X size={20} />
          </button>
          <div className="max-w-full max-h-full overflow-auto" onClick={e => e.stopPropagation()}>
            <img src={product.image} alt={product.name}
              className="max-w-none md:max-w-[95vw] max-h-[92vh] w-auto h-auto object-contain rounded-lg shadow-[0_40px_120px_-10px_rgba(255,47,161,0.4)]" />
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono-label text-[10px] text-neutral-400 bg-black/60 px-3 py-1.5 rounded">
            TAP OUTSIDE OR ESC TO CLOSE
          </div>
        </div>
      )}
    </main>
  );
};

export default ProductDetail;
